import React from "react";
import Icon from "./AppIcon";
import Input from "./ui/Input";

// ---------------------------------------------------------------------------
// Shared interview-campaign form (create + edit).
//
// A campaign is the reusable "interview definition" (role, type, difficulty,
// topics, duration, deadline). It is authored ONCE and fanned out to many
// per-student assignments, so this same field set backs both the create modal
// on the Campaigns list and the edit modal on the CampaignDetail page.
//
// The component is intentionally presentational: the parent owns `form` state,
// wraps it in its own <form> (for Enter-to-submit), and calls the payload /
// validation helpers exported here. That keeps submission logic with the modal
// footer buttons and avoids nested <form> elements.
// ---------------------------------------------------------------------------

// Native <select>/<textarea> styling copied verbatim from Students.jsx so the
// controls are pixel-identical to the rest of the portal (and the Input primitive).
const selectClasses =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

const textareaClasses =
  "flex min-h-[88px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

// Interview-type values mirror the seeded `interview_types` table the interview
// engine references (CODING / SYSTEM_DESIGN / HR). Storing these exact values
// means the campaign can be handed straight to the existing session engine
// without a translation layer. This static list is now only a FALLBACK — the
// live options are fetched from the DB (GET /api/institution/interview-types) and
// passed in via the `interviewTypes` prop, so admins who add new types on the
// platform see them here automatically and every campaign maps to a real flow.
export const INTERVIEW_TYPE_OPTIONS = [
  { value: "CODING", label: "Coding / DSA" },
  { value: "SYSTEM_DESIGN", label: "System Design" },
  { value: "HR", label: "HR / Behavioral" },
];

// Nice human labels for the canonical seeded types; anything else is title-cased
// from its raw name (e.g. "DATA_SCIENCE" -> "Data Science").
const KNOWN_TYPE_LABELS = Object.fromEntries(
  INTERVIEW_TYPE_OPTIONS.map((t) => [t.value, t.label])
);

export function interviewTypeLabel(value) {
  if (!value) return "";
  if (KNOWN_TYPE_LABELS[value]) return KNOWN_TYPE_LABELS[value];
  return String(value)
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// normalizeTypeOptions turns the DB rows ({name, description}) into {value,label}
// pairs, falling back to the static list when the catalog hasn't loaded yet.
function normalizeTypeOptions(interviewTypes) {
  if (Array.isArray(interviewTypes) && interviewTypes.length > 0) {
    return interviewTypes.map((t) => ({
      value: t.name,
      label: interviewTypeLabel(t.name),
    }));
  }
  return INTERVIEW_TYPE_OPTIONS;
}

export const DIFFICULTY_OPTIONS = ["Easy", "Medium", "Hard"];

export const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "closed", label: "Closed" },
  { value: "archived", label: "Archived" },
];

export const EMPTY_CAMPAIGN = {
  placementSessionId: "",
  name: "",
  role: "",
  company: "",
  interviewType: "CODING",
  duration: "30",
  difficulty: "Medium",
  topics: "",
  instructions: "",
  deadline: "",
  status: "draft",
};

// ── datetime <-> <input type="datetime-local"> helpers ──────────────────────
// The API speaks RFC3339 (e.g. "2026-09-01T09:00:00Z"); the datetime-local
// input speaks bare local "YYYY-MM-DDTHH:mm". We convert on the boundary so the
// admin always sees/edits their own local time while the wire stays UTC.

export function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export function localInputToIso(local) {
  if (!local) return null;
  // A datetime-local string with no zone is parsed as LOCAL time (spec), which
  // is exactly what the admin means; toISOString() then normalizes to UTC.
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function parseTopics(raw) {
  if (Array.isArray(raw)) return raw.map((t) => String(t).trim()).filter(Boolean);
  return String(raw || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

// campaignToForm maps an API campaign object into editable form state.
export function campaignToForm(c) {
  return {
    placementSessionId: c?.placementSessionId || "",
    name: c?.name || "",
    role: c?.role || "",
    company: c?.company || "",
    interviewType: c?.interviewType || "CODING",
    duration: c?.duration != null ? String(c.duration) : "30",
    difficulty: c?.difficulty || "Medium",
    topics: Array.isArray(c?.topics) ? c.topics.join(", ") : "",
    instructions: c?.instructions || "",
    deadline: isoToLocalInput(c?.deadlineAt),
    status: c?.status || "draft",
  };
}

// validateCampaignForm returns a user-facing error string, or "" when valid.
export function validateCampaignForm(form, { requireBatch = false } = {}) {
  if (requireBatch && !form.placementSessionId) {
    return "Choose a batch for this campaign.";
  }
  if (!form.name || !form.name.trim()) {
    return "Campaign name is required.";
  }
  if (form.duration !== "" && form.duration != null) {
    const d = parseInt(form.duration, 10);
    if (Number.isNaN(d) || d <= 0) {
      return "Duration must be a positive number of minutes.";
    }
  }
  if (form.deadline && !localInputToIso(form.deadline)) {
    return "The deadline date looks invalid.";
  }
  return "";
}

// buildCampaignPayload shapes the form into the API request body.
//
//  • create (isEdit=false): optional text/deadline fields are omitted when
//    empty so the DB column defaults / NULLs apply.
//  • edit   (isEdit=true):  text fields are always sent (an empty string clears
//    them via the handler's COALESCE-of-the-value); topics are always sent
//    (empty array clears). The deadline can be updated or cleared (an empty string
//    signals the handler to clear the deadline and propagate to assignments).
export function buildCampaignPayload(form, { isEdit = false } = {}) {
  const trim = (s) => String(s || "").trim();
  const payload = {
    name: trim(form.name),
    interviewType: form.interviewType || "CODING",
    difficulty: form.difficulty || "Medium",
    topics: parseTopics(form.topics),
    status: form.status || "draft",
  };

  const dur = parseInt(form.duration, 10);
  if (!Number.isNaN(dur) && dur > 0) payload.duration = dur;

  if (!isEdit) {
    payload.placementSessionId = form.placementSessionId;
    const role = trim(form.role);
    if (role) payload.role = role;
    const company = trim(form.company);
    if (company) payload.company = company;
    const instructions = trim(form.instructions);
    if (instructions) payload.instructions = instructions;
    const iso = form.deadline ? localInputToIso(form.deadline) : null;
    if (iso) payload.deadlineAt = iso;
  } else {
    payload.role = trim(form.role);
    payload.company = trim(form.company);
    payload.instructions = trim(form.instructions);
    payload.deadlineAt = form.deadline ? localInputToIso(form.deadline) || "" : "";
  }

  return payload;
}

// ── Presentational field set ────────────────────────────────────────────────

export default function CampaignForm({
  form,
  onField,
  batches = [],
  lockBatch = false,
  error,
  interviewTypes = [],
  companies = [],
  referenceLoading = false,
}) {
  const typeOptions = normalizeTypeOptions(interviewTypes);
  // Preserve a legacy/custom interviewType (e.g. the old 'General' default) that
  // isn't in the current catalog so editing a campaign never silently changes it.
  const typeValues = typeOptions.map((t) => t.value);
  const fullTypeOptions =
    form.interviewType && !typeValues.includes(form.interviewType)
      ? [
          { value: form.interviewType, label: interviewTypeLabel(form.interviewType) },
          ...typeOptions,
        ]
      : typeOptions;

  // Company is optional and stored as free text on the campaign, but we offer the
  // platform's company catalog as the picker so the value maps to a real,
  // engine-recognized company. On EDIT an existing campaign may hold a company
  // that isn't (or is no longer) in the catalog — keep it selectable so saving
  // doesn't silently drop it.
  const companyNames = companies.map((co) => co.name).filter(Boolean);
  const companyOptions =
    form.company && !companyNames.includes(form.company)
      ? [form.company, ...companyNames]
      : companyNames;

  return (
    <div className="space-y-4">
      {/* Batch */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          Batch <span className="text-destructive">*</span>
        </label>
        <select
          value={form.placementSessionId}
          onChange={onField("placementSessionId")}
          disabled={lockBatch}
          className={selectClasses}
        >
          <option value="" disabled>
            Select a batch
          </option>
          {batches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        {lockBatch && (
          <p className="text-xs text-muted-foreground">
            A campaign&apos;s batch can&apos;t be changed. Create a new campaign to
            target another batch.
          </p>
        )}
      </div>

      <Input
        label="Campaign name"
        required
        placeholder="e.g. Software Engineer — Round 1"
        value={form.name}
        onChange={onField("name")}
        autoFocus
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Role"
          placeholder="e.g. Software Engineer"
          value={form.role}
          onChange={onField("role")}
        />
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Company</label>
          <select
            value={form.company}
            onChange={onField("company")}
            className={selectClasses}
          >
            <option value="">
              {referenceLoading ? "Loading companies…" : "No specific company"}
            </option>
            {companyOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            Optional — the engine tailors questions to this company.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Interview type</label>
          <select
            value={form.interviewType}
            onChange={onField("interviewType")}
            className={selectClasses}
          >
            {fullTypeOptions.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            {referenceLoading
              ? "Loading types…"
              : "Determines the interview flow each student will get."}
          </p>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Difficulty</label>
          <select
            value={form.difficulty}
            onChange={onField("difficulty")}
            className={selectClasses}
          >
            {DIFFICULTY_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Duration (minutes)"
          type="number"
          min="1"
          value={form.duration}
          onChange={onField("duration")}
        />
        <Input
          label="Deadline"
          type="datetime-local"
          description="Optional — the default due date for assignments."
          value={form.deadline}
          onChange={onField("deadline")}
        />
      </div>

      <Input
        label="Topics"
        placeholder="e.g. Arrays, Dynamic Programming, SQL"
        description="Comma-separated. Reused for every student in this campaign."
        value={form.topics}
        onChange={onField("topics")}
      />

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Instructions</label>
        <textarea
          rows={3}
          placeholder="Optional notes shown to students before they start."
          value={form.instructions}
          onChange={onField("instructions")}
          className={textareaClasses}
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Status</label>
        <select
          value={form.status}
          onChange={onField("status")}
          className={selectClasses}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground">
          Draft campaigns are hidden from students. Set to Active when you&apos;re
          ready for them to start.
        </p>
      </div>

      {error && (
        <p className="flex items-center gap-1.5 text-sm text-destructive">
          <Icon name="AlertCircle" size={14} /> {error}
        </p>
      )}
    </div>
  );
}
